import AdminPageBackground from "@/components/admin/AdminPageBackground";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Eye, EyeOff, LayoutGrid, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useSectionVisibility, SectionVisibility } from "@/hooks/useSectionVisibility";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SortableItemProps {
  section: SectionVisibility;
  isRTL: boolean;
  onToggle: (key: string, value: boolean) => void;
}

const SortableItem = ({ section, isRTL, onToggle }: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
        section.is_visible
          ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800"
          : "bg-gray-50 border-gray-200 dark:bg-gray-950/20 dark:border-gray-700"
      } ${isDragging ? "shadow-lg ring-2 ring-primary" : ""}`}
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
        >
          <GripVertical className="text-gray-400" size={20} />
        </button>
        {section.is_visible ? (
          <Eye className="text-green-600" size={20} />
        ) : (
          <EyeOff className="text-gray-400" size={20} />
        )}
        <Label
          htmlFor={section.section_key}
          className="text-base font-medium cursor-pointer"
        >
          {isRTL ? section.section_name_ar : section.section_name_en}
        </Label>
      </div>
      <Switch
        id={section.section_key}
        checked={section.is_visible}
        onCheckedChange={(checked) => onToggle(section.section_key, checked)}
      />
    </div>
  );
};

const SectionVisibilityPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const { sections, isLoading, toggleVisibility, updateOrder } = useSectionVisibility();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => s.id === active.id);
      const newIndex = sections.findIndex((s) => s.id === over.id);
      const reordered = arrayMove(sections, oldIndex, newIndex);
      updateOrder(reordered);
    }
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <Button
          variant="outline"
          onClick={() => navigate("/admin-dashboard")}
          className="flex items-center gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20"
        >
          <ArrowLeft size={16} className={isRTL ? "rotate-180" : ""} />
          {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
        </Button>
        <div className="flex items-center gap-3">
          <LayoutGrid className="text-primary" size={24} />
          <h1 className="text-xl font-bold text-white">
            {isRTL ? "إدارة أقسام الموقع" : "Section Visibility Management"}
          </h1>
        </div>
        <div className="w-[100px]"></div>
      </div>

      <div className="container mx-auto p-6 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {isRTL ? "إظهار وإخفاء الأقسام" : "Show/Hide Sections"}
            </CardTitle>
            <CardDescription>
              {isRTL
                ? "تحكم في الأقسام التي تظهر في الصفحة الرئيسية. اسحب لإعادة الترتيب."
                : "Control which sections appear on homepage. Drag to reorder."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                    <Skeleton className="h-6 w-40" />
                    <Skeleton className="h-6 w-12" />
                  </div>
                ))}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sections.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {sections.map((section) => (
                      <SortableItem
                        key={section.id}
                        section={section}
                        isRTL={isRTL}
                        onToggle={toggleVisibility}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminPageBackground>
  );
};

export default SectionVisibilityPage;
